/**
 * Admin — Pending Registrations
 * Users who started signup but haven't clicked their verification link yet.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Trash2, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "-"; }
};

export const AdminPendingRegistrations = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pending-registrations`, { headers: adminHeaders() });
      setItems(res.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/sportly-admin");
        return;
      }
      toast.error("Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/sportly-admin");
      return;
    }
    load();
  }, [load, navigate]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/pending-registrations/${deleteTarget.id}`, { headers: adminHeaders() });
      setItems((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success("Verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-pending-page">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/sportly-admin/dashboard")} className="text-slate-300 hover:text-white hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Terug
          </Button>
          <h1 className="text-lg font-semibold flex-1">Onbevestigde accounts</h1>
          <Button onClick={load} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="pending-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-1.5" /> Uitloggen
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <p className="text-sm text-slate-400 mb-4">
          Deze mensen hebben zich aangemeld maar hebben de verificatielink nog niet aangeklikt. Verlopen records worden gemarkeerd; onze cleanup verwijdert ze zodra ze meer dan 24 uur oud zijn.
        </p>
        <div className="rounded-lg border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-table">
              <thead>
                <tr className="bg-slate-900/60 border-b border-slate-800">
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">E-mail</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Naam</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Plan</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Code / Invite</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Gestart</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Verloopt</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Status</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400">Actie</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-6 text-slate-500">Laden...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-6 text-slate-500" data-testid="pending-empty">Geen onbevestigde aanmeldingen</td></tr>
                ) : items.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-900/30" data-testid={`pending-row-${p.id}`}>
                    <td className="px-3 py-2.5 text-slate-200">{p.email}</td>
                    <td className="px-3 py-2.5 text-slate-300">{p.name || "-"}</td>
                    <td className="px-3 py-2.5 text-slate-400">
                      {p.selected_plan ? (
                        <span className="capitalize">{p.selected_plan}</span>
                      ) : "-"}
                      {p.billing_cycle ? <span className="text-xs text-slate-500 ml-1">/{p.billing_cycle === "yearly" ? "jaar" : "mnd"}</span> : null}
                    </td>
                    <td className="px-3 py-2.5">
                      {p.discount_code ? (
                        <span className="font-mono text-xs text-amber-300">{p.discount_code}</span>
                      ) : p.invite_id ? (
                        <span className="text-xs text-emerald-300">invite</span>
                      ) : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-400 text-xs">{formatDate(p.created_at)}</td>
                    <td className="px-3 py-2.5 text-slate-400 text-xs">{formatDate(p.expires_at)}</td>
                    <td className="px-3 py-2.5">
                      {p.expired ? (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-rose-500/20 text-rose-300">Verlopen</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-300">Wacht op klik</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`pending-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Onbevestigde aanmelding verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              De gebruiker zal opnieuw moeten registreren. Deze actie is niet ongedaan te maken.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-pending-delete">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPendingRegistrations;
